import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { Timer } from "@/components/Timer";
import { useFreezableCountdown } from "@/hooks/useCountdown";
import { cancelSpeech } from "@/lib/speech";
import { startRecording, transcribe, micErrorKind } from "@/lib/recorder";
import { StatusBadge, useItemAudio, ReplayButton, Prompt, AudioFailedPanel } from "@/components/items/SpeakingItem";

const MicPanel = ({ mic, level, phase }) => {
  if (mic === "denied" || mic === "nodevice" || mic === "error") return (
    <div data-testid="mic-blocked-panel" className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
      <MicOff className="mt-0.5 h-5 w-5 shrink-0" />
      <div><b>{mic === "nodevice" ? "No microphone found" : "Microphone blocked"}</b>
        <p className="text-xs">Speaking answers can only be given by voice. This item will be scored as no response. {mic === "nodevice" ? "Connect a microphone" : "Allow the microphone via the lock icon in the address bar"}, then reload the page.</p></div>
    </div>
  );
  const rec = mic === "recording";
  return (
    <div data-testid="mic-record-panel" data-mic-state={mic} className={`flex items-center gap-4 rounded-xl border-2 p-5 transition-colors ${rec ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-slate-50"}`}>
      <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-full ${rec ? "animate-pulse bg-rose-500 text-white" : "bg-slate-200 text-slate-500"}`}><Mic className="h-6 w-6" /></span>
      <div className="flex-1 space-y-2">
        <div data-testid="mic-status-label" className={`text-sm font-bold ${rec ? "text-rose-700" : "text-slate-600"}`}>
          {phase !== "answer" ? (phase === "failed" ? "Waiting for the prompt" : "Microphone opens when the audio finishes") : rec ? "Recording — speak now" : "Opening microphone…"}
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-200"><div data-testid="mic-level-meter" className="h-full bg-rose-500 transition-[width] duration-75" style={{ width: `${rec ? level : 0}%` }} /></div>
      </div>
    </div>
  );
};

export const SpokenItem = ({ part, item, mode, rate, onSubmit }) => {
  const { phase, play, grace } = useItemAudio(part, item, rate, part.audio);
  const [mic, setMic] = useState("idle");
  const [level, setLevel] = useState(0);
  const [sending, setSending] = useState(false);
  const recRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    if (phase !== "answer") return undefined;
    let cancelled = false;
    setMic("starting");
    startRecording(setLevel).then((r) => {
      if (cancelled || doneRef.current) { r.stop(); return; }
      recRef.current = r; setMic("recording");
    }).catch((e) => !cancelled && setMic(micErrorKind(e)));
    return () => { cancelled = true; if (!doneRef.current) { recRef.current?.stop(); recRef.current = null; } };
  }, [phase]);

  const freezeRef = useRef(() => {});
  const submit = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    freezeRef.current();
    cancelSpeech();
    setLevel(0);
    setSending(true);
    const r = recRef.current;
    if (!r) { onSubmit({ text: "", spoken: true, noMic: true }); return; }
    r.stop().then((res) => {
      const pending = transcribe(res).catch(() => "");
      const audioUrl = mode === "practice" && res.blob ? URL.createObjectURL(res.blob) : undefined;
      onSubmit({ text: "", spoken: true, pending, ...(audioUrl ? { audioUrl } : {}) });
    });
  }, [onSubmit, mode]);

  // Read Aloud allows ~130 words/min so long hard passages stay readable
  const total = part.key === "A" ? Math.max(part.time, Math.ceil(item.text.split(/\s+/).length / 2.2)) : part.time;
  const [left, freeze] = useFreezableCountdown(total, phase === "answer", submit);
  freezeRef.current = freeze;
  useEffect(() => {
    if (phase !== "answer") return undefined;
    const h = (e) => { if (e.key === "Enter" && mic === "recording") { e.preventDefault(); submit(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [phase, mic, submit]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3"><StatusBadge phase={phase} />
          {mode === "practice" && part.audio && <ReplayButton onClick={play} disabled={phase === "listening" || mic === "recording"} />}
        </div>
        <Timer left={left} total={total} label={sending ? "Stopped" : phase === "answer" ? "Response time" : "Waiting for audio"} />
      </div>
      <Prompt part={part} item={item} phase={phase} />
      {phase === "failed" && <AudioFailedPanel onReplay={play} grace={grace} />}
      <MicPanel mic={mic} level={level} phase={phase} />
      {sending ? (
        <div data-testid="spoken-submitting" role="status" className="flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-semibold text-indigo-800"><Loader2 className="h-4 w-4 animate-spin" />{mode === "practice" ? "Analyzing your response…" : "Response saved — loading the next question…"}</div>
      ) : (
        <button data-testid="item-submit-btn" onClick={submit} disabled={phase !== "answer" || mic === "starting" || mic === "idle"} className="rounded-full bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-40">
          {["denied", "nodevice", "error"].includes(mic) ? "Skip item" : "Done speaking"}
        </button>
      )}
    </div>
  );
};
